<template>
  <div v-if="!documentSigned && !showFormA" class="skribble-form-k card p-4">
    <div>
      <h3 class="mb-3">{{ $t('skr_formk_title') }}</h3>

      <form v-if="form" @submit.prevent="sendForSigning">
      <!-- Contracting Partner Info -->
      <div class="form-row">
        <div class="form-group col-6">
          <label>{{ $t('skr_first_name') }}</label>
          <input
              v-model="form.sign_first"
              class="form-control"
              :class="{ 'is-invalid': invalidFields['partner_first'] }"
              type="text"
              ref="partner_first"
          />
        </div>
        <div class="form-group col-6">
          <label>{{ $t('skr_last_name') }}</label>
          <input
              v-model="form.sign_last"
              class="form-control"
              :class="{ 'is-invalid': invalidFields['partner_last'] }"
              type="text"
              ref="partner_last"
          />
        </div>
      </div>
      <div class="form-group">
        <label>{{ $t('skr_address') }}</label>
        <input
            v-model="form.sign_address"
            class="form-control"
            :class="{ 'is-invalid': invalidFields['partner_address'] }"
            type="text"
            ref="partner_address"
        />
      </div>

      <!-- Declaration Options -->
      <div class="form-group mt-3">
        <label>{{ $t('skr_declare_choose') }}</label>
        <div class="form-check mb-1" v-for="opt in declarationOptions" :key="opt.value">
          <input class="form-check-input" type="radio" :id="'dec_' + opt.value" :value="opt.value" v-model="form.declarationType" />
          <label class="form-check-label" :for="'dec_' + opt.value">{{ $t(opt.label) }}</label>
        </div>
      </div>

      <!-- Persons Section -->
      <div class="owners-area mt-2">
        <h5>{{ $t('skr_persons_title', 'Person(s)') }}</h5>
        <div v-for="(p, idx) in form.persons" :key="idx" class="owner-card p-2 mb-2 border rounded">
          <div class="d-flex justify-content-between align-items-start">
            <strong>Person #{{ idx + 1 }}</strong>
            <button v-if="form.persons.length > 1" type="button" class="btn btn-sm btn-outline-danger" @click="removePerson(idx)">
              {{ $t('skr_remove') }}
            </button>
          </div>

          <div class="form-row mt-2">
            <div class="form-group col-md-6">
              <label>{{ $t('skr_owner_last') }}</label>
              <input
                  v-model="p.lastName"
                  class="form-control"
                  :class="{ 'is-invalid': invalidFields[`person_${idx}_last`] }"
                  type="text"
                  :ref="`person_${idx}_last`"
              />
            </div>
            <div class="form-group col-md-6">
              <label>{{ $t('skr_owner_first') }}</label>
              <input
                  v-model="p.firstName"
                  class="form-control"
                  :class="{ 'is-invalid': invalidFields[`person_${idx}_first`] }"
                  type="text"
                  :ref="`person_${idx}_first`"
              />
            </div>
            <div class="form-group col-md-12">
              <label>{{ $t('skr_owner_address') }}</label>
              <input
                  v-model="p.actualAddress"
                  class="form-control"
                  :class="{ 'is-invalid': invalidFields[`person_${idx}_address`] }"
                  type="text"
                  :ref="`person_${idx}_address`"
              />
            </div>
          </div>
        </div>

        <div class="text-right">
          <button type="button" class="btn btn-sm btn-outline-primary" @click="addPerson">
            {{ $t('skr_add_person') }}
          </button>
        </div>
      </div>

      <!-- Fiduciary Question -->
      <div class="form-group mt-3">
        <label>{{ $t('skr_fiduciary_q') }}</label>
        <div class="form-check">
          <input class="form-check-input" type="radio" id="fid_no" value="no" v-model="form.fiduciary" />
          <label class="form-check-label" for="fid_no">{{ $t('no') }}</label>
        </div>
        <div class="form-check">
          <input class="form-check-input" type="radio" id="fid_yes" value="yes" v-model="form.fiduciary" />
          <label class="form-check-label" for="fid_yes">{{ $t('yes') }}</label>
        </div>
        <p v-if="form.fiduciary === 'yes'" class="mt-2 small">
          {{ $t('skr_fiduciary_note') }}
        </p>
      </div>

      <!-- Submit -->
      <div class="mt-2">
        <div v-if="error" class="alert text-danger">{{ error }}</div>
        <button :disabled="loading" class="btn btn-primary">
          <span v-if="loading">
            <img width="50" height="50" src="/new-loader.svg" alt="loader" />
          </span>
          <span v-else>{{ $t('skr_btn') }}</span>
        </button>
        <p v-if="skribbleError" class="text-danger mt-2" style="text-align: right;">
          {{ $t(skribbleError) }}
        </p>
      </div>
    </form>

      <SkribblePopup
          :show="showPopup"
          @cancel="showPopup = false"
          @continue="handlePopupConfirm"
      />
    </div>
  </div>

  <div v-else-if="showFormA">
    <skribble-form-a></skribble-form-a>
  </div>

  <div v-else class="p-4 text-center">
    <h3 class="text-success text-large">{{ $t('skr_document_signed') }}</h3>
  </div>
</template>

<script>
import SkribbleFormA from '@/components/skribble/SkribbleFormA.vue'

export default {
  name: 'SkribbleFormK',
  components: {
    SkribbleFormA
  },
  data() {
    return {
      loading: false,
      documentSigned: false,
      showFormA: false,
      showPopup: false,
      pollInterval: null,
      skribbleError: null,
      error: null,
      invalidFields: {},
      declarationOptions: [
        { value: 'shares', label: 'skr_formk_opt_shares' },
        { value: 'control', label: 'skr_formk_opt_control' },
        { value: 'managing', label: 'skr_formk_opt_managing' },
      ],
      country: '',
      form: null
    }
  },
  async created() {
    const user = await this.refreshUser();
    const company = user.company || {};
    let countryName = user.country.name;
    this.country = countryName;

    const address = [
      user.address || '',
      user.address_nr || '',
      user.zip || '',
      user.city || ''
    ].filter(Boolean).join(', ');

    this.form = {
      sign_first: user.first_name || '',
      sign_last: user.last_name || '',
      sign_address: countryName ? `${address}, ${countryName}` : address,
      lang: this.$i18n.locale ? this.$i18n.locale : "de",
      declarationType: 'shares',
      persons: [
        { lastName: '', firstName: '', actualAddress: '' }
      ],
      fiduciary: 'no',
      date: new Date().toISOString().slice(0, 10),
      userID: user.id || '',
      companyID: company.id || ''
    }
  },

  methods: {
    async refreshUser() {
      try {
        let user = this.$store.state.auth.user;
        const { data } = await this.$http.post(`/getUserDataWithId/${user.id}`);

        if (data && data.value) {
          this.$store.commit("auth/SET_USER", data.value);
          return data.value;
        } else {
          console.warn("No user returned from backend");
          return user;
        }
      } catch (err) {
        console.error("Failed to refresh user:", err);
        return this.$store.state.auth.user;
      }
    },

    addPerson() {
      this.form.persons.push({ lastName: '', firstName: '', actualAddress: '' })
    },

    removePerson(idx) {
      this.form.persons.splice(idx, 1)
    },

    async sendForSigning() {
      this.error = null
      this.skribbleError = null
      this.invalidFields = {}

      // validate partner
      if (!this.form.sign_first) this.invalidFields['partner_first'] = true
      if (!this.form.sign_last) this.invalidFields['partner_last'] = true
      if (!this.form.sign_address) this.invalidFields['partner_address'] = true

      // validate persons
      this.form.persons.forEach((p, idx) => {
        if (!p.lastName) this.invalidFields[`person_${idx}_last`] = true
        if (!p.firstName) this.invalidFields[`person_${idx}_first`] = true
        if (!p.actualAddress) this.invalidFields[`person_${idx}_address`] = true
      })

      // if errors → scroll to first invalid field
      const firstInvalid = Object.keys(this.invalidFields)[0]
      if (firstInvalid) {
        this.error = this.$t('skr_error_fill_required')
        this.$nextTick(() => {
          const el = this.$refs[firstInvalid]
          if (Array.isArray(el)) el[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          else el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          if (Array.isArray(el)) el[0]?.focus()
          else el?.focus()
        })
        return
      }

      // continue sending
      this.loading = true
      try {
        this.form.lang = this.$i18n.locale ? this.$i18n.locale : 'de';
        const payload = { form: this.form, form_type: 'formK' }
        const res = await this.$http.post('/skribble/send-for-signing', payload)
        const signingUrl = res?.data?.value?.signing_url || null

        if (!signingUrl) {
          this.skribbleError = "Internal server error";
        } else if (signingUrl) {
          this.signing_url = signingUrl;
          this.handleButtonClick(signingUrl)
        }
      } catch (err) {
        console.error(err)
        this.skribbleError = err.response?.data?.message || err.message || 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },

    handlePopupConfirm() {
      this.showPopup = false;
      window.open(this.signing_url, '_blank');
      this.pollInterval = setInterval(this.checkSigningStatus, 10000)
    },

    handleButtonClick(signingUrl) {
      if (localStorage.getItem("hideSkribblePopup") === "true") {
        window.open(signingUrl, '_blank');
        this.pollInterval = setInterval(this.checkSigningStatus, 10000)
      } else {
        this.showPopup = true;
      }
    },

    checkSigningStatus() {
      this.$http.post(`/skribble/check-status/${this.form.userID}/formK`).then((res) => {
        if (res.data?.value === 'SIGNED') {
          clearInterval(this.pollInterval)
          this.pollInterval = null
          if (this.form.fiduciary === 'yes') {
            this.showFormA = true
          } else {
            this.documentSigned = true
            window.location.reload();
          }
        }
      }).catch(err => {
        console.error('Error checking signing status', err)
      });
    }
  }
}
</script>

<style scoped>

.skribble-form-k {
  max-width: 820px;
  margin: 0 auto;
  background: #e1e1e12e;
}

.owner-card {
  background: #fbfbfb;
}

.is-invalid {
  border: 1px solid red !important;
}
</style>
